import { propKey, type PropLine } from '@sharpr/pricing';
import type { MarketCode, Sport } from '@sharpr/types';
import type { Database, Tables } from '@sharpr/types/database';
import type { SupabaseClient } from '@supabase/supabase-js';

// The board's live stream (spec §13.3–13.4): one Centrifugo channel per sport, identical for every viewer. Paid data
// never rides it; the Seed (the board's snapshot, @sharpr/projections) carries that.
export const boardChannel = (sport: Sport) => `board:${sport}` as const;

// Where the channel's history is at (Centrifugo's offset and epoch). A changed epoch means the history was lost, so
// offsets from before it compare with nothing.
export interface StreamPosition { offset: number; epoch: string }

// Position `a` is at or after `b`. Another epoch compares with nothing, so it counts as newer; so does no position.
export const reached = (a: StreamPosition | undefined, b: StreamPosition): boolean => !a || a.epoch !== b.epoch || a.offset >= b.offset;

// What a reseed says changed (spec §3.7): its meaning is always "refetch the seed".
export type ReseedScope = 'projection' | 'visibility' | 'notes' | 'takeover';

// A game whose lines the worker stopped writing (it went live): every held line of the game older than it is gone.
export interface Tombstone { gameId: string; updatedAt: number; removed: true }
export interface Delta { type: 'props'; v: 1; sport: Sport; propLines: Array<PropLine | Tombstone> }
// A reseed is a publication like any other, with its own offset: a board refetches until it holds a seed read at or after it.
// games: the changed games and the board version each one's cut was stored under; sportVersion: the sport's, as of them.
// Absent when the writer could not store the cuts: refetch the seed.
export interface Reseed { type: 'reseed'; sport: Sport; scopes: ReseedScope[]; games?: Record<string, number>; sportVersion?: number }

export type PropLineRow = Omit<Tables<'prop_lines'>, 'id'>;

// A stored quote (a prop line, or one of its moves) as a PropLine's quote: either side may be missing (a book offering
// one side of its main line); removed is the book's pull.
export type Quote = Pick<PropLine, 'line' | 'over' | 'under' | 'removed'>;
export function quote(q: { line: number; over?: number | null; under?: number | null; removed: boolean }): Quote {
  return { line: q.line, over: q.over ?? undefined, under: q.under ?? undefined, removed: q.removed || undefined };
}
export function quoteFromRow(row: Pick<PropLineRow, 'line' | 'over_odds' | 'under_odds' | 'is_removed'>): Quote {
  return quote({ line: row.line, over: row.over_odds, under: row.under_odds, removed: row.is_removed });
}

// One book's quote of one prop, as the board holds it: the seed's lines and the worker's deltas both encode through
// here. A missing side, limit or link is absent; a pull is `removed`.
export interface PropLineFields {
  gameId: string; playerId: string; market: MarketCode; book: string; line: number;
  over?: number | null; under?: number | null; overLimit?: number | null; underLimit?: number | null;
  overLink?: string | null; underLink?: string | null; removed: boolean; updatedAt: string;
}
export function propLine(f: PropLineFields): PropLine {
  return {
    key: propKey(f.gameId, f.playerId, f.market), market: f.market, book: f.book, ...quote(f),
    overLimit: f.overLimit ?? undefined, underLimit: f.underLimit ?? undefined, overLink: f.overLink ?? undefined, underLink: f.underLink ?? undefined,
    updatedAt: Date.parse(f.updatedAt),
  };
}

export function propLineFromRow(row: PropLineRow): PropLine {
  return propLine({
    gameId: row.game_id, playerId: row.player_id, market: row.market as MarketCode, book: row.bookmaker, line: row.line,
    over: row.over_odds, under: row.under_odds, overLimit: row.over_bet_limit, underLimit: row.under_bet_limit,
    overLink: row.over_link, underLink: row.under_link, removed: row.is_removed, updatedAt: row.updated_at,
  });
}

// read_board_lines' positional arrays: each open game's players once (what the seed reads of a player, and his game's
// tip-off, never vary across his lines), and each line's own fields. The column order is the SQL function's contract.
export const BOARD_PLAYER_COLUMNS = ['game_id', 'player_id', 'player_name', 'position', 'team_id', 'opponent_team_id', 'is_home', 'commence_time'] as const;
export const BOARD_LINE_COLUMNS = ['game_id', 'player_id', 'market', 'bookmaker', 'line', 'over_odds', 'under_odds', 'over_bet_limit', 'under_bet_limit',
  'over_link', 'under_link', 'is_removed', 'updated_at'] as const;
type BoardPlayer = { [K in typeof BOARD_PLAYER_COLUMNS[number]]: PropLineRow[K] };
type BoardLine = { [K in typeof BOARD_LINE_COLUMNS[number]]: PropLineRow[K] };
export interface BoardLines { players: unknown[][]; lines: unknown[][] }

// The board's lines as the odds worker last published them (spec: board delivery, v3): read_board_lines' answer, with the
// channel position read before it (so a board resumes the stream from there and misses nothing) and when it was built.
// One object per sport per scope, gzipped JSON, overwritten on each build; requests read it and never read prop_lines.
export interface LinesSnapshot { v: 1; sport: Sport; position?: StreamPosition; builtAt: string; lines: BoardLines }
export const linesSnapshotKey = (scope: string, sport: Sport) => `${scope}/${sport}/lines.json.gz`;

const decode = <C extends readonly string[]>(columns: C, values: readonly unknown[]) =>
  Object.fromEntries(columns.map((c, i) => [c, values[i]])) as { [K in C[number]]: unknown };

// read_board_lines' answer as the rows of games the schedule holds open and not yet started: each line with its player's
// fields joined back on.
export function boardRows(sport: Sport, { players, lines }: BoardLines): PropLineRow[] {
  const byPlayer = new Map<string, BoardPlayer>();
  for (const values of players) {
    const p = decode(BOARD_PLAYER_COLUMNS, values) as BoardPlayer;
    byPlayer.set(`${p.game_id}|${p.player_id}`, p);
  }
  return lines.map(values => {
    const l = decode(BOARD_LINE_COLUMNS, values) as BoardLine;
    const p = byPlayer.get(`${l.game_id}|${l.player_id}`);
    if (!p) throw new Error(`read_board_lines: line of ${l.game_id}|${l.player_id} has no player`);
    return { sport, ...p, ...l };
  });
}

// The board's lines: the rows of games the schedule holds open and not yet started.
export async function readBoardPropLines(client: SupabaseClient<Database>, sport: Sport): Promise<PropLineRow[]> {
  const { data } = await client.rpc('read_board_lines', { p_sport: sport }).throwOnError();
  return boardRows(sport, data as unknown as BoardLines);
}
